import { useCallback, useMemo, useRef, useState } from 'react';
import SimpleContainer from '../../simpleComponents/SimpleContainer';
import SimpleInput from '../../simpleComponents/SimpleInput';
import SimpleScrollView from '../../simpleComponents/SimpleScrollView';
import useHttpRequest from '../../../hooks/useHttpRequest';
import SecondaryButton from '../buttons/SecondaryButton';
import SimpleTextArea from '../../simpleComponents/SimpleTextArea';
import PrimaryButton from '../buttons/PrimaryButton';
import SearchInput from '../../specializedComponents/containers/SearchInput';
import { customersApi } from '../../../api/customersApi';
import casesApi, { casesTypeApi } from '../../../api/casesApi';
import { buttonSizes } from '../../../styles/buttons/buttonSizes';
import { adminApi } from '../../../api/adminApi';
import { toNativeDateValue } from '../../../functions/date/formatDateForInput';
import IsraeliPhoneNumberValidation from '../../../functions/validation/IsraeliPhoneNumberValidation';
import emailValidation from '../../../functions/validation/EmailValidation';

import './CaseFullView.scss';
import useAutoHttpRequest from '../../../hooks/useAutoHttpRequest';
import { useTranslation } from 'react-i18next';
import { usePopup } from '../../../providers/PopUpProvider';
import { useFirmPermissions } from '../../../providers/FirmPermissionsProvider';
import ConfirmationDialog from '../popups/ConfirmationDialog';
import ClientPopup from '../../../screens/mainScreen/components/ClientPopUp';
import SimplePopUp from '../../simpleComponents/SimplePopUp';
import CaseSigningFlow from '../../../screens/signingScreen/templates/CaseSigningFlow';
import { useSigningV2Available } from '../../../screens/signingScreen/templates/SigningPackagesHub';

function _buildInitialCaseData(caseDetails, initialDraft) {
    const source = caseDetails?.CaseId ? caseDetails : (initialDraft || caseDetails || {});
    return {
        CaseId: source.CaseId || '',
        CaseName: source.CaseName || '',
        CourtCaseNumber: source.CourtCaseNumber || '',
        CaseTypeId: source.CaseTypeId || '',
        CaseTypeName: source.CaseTypeName || '',
        CompanyName: source.CompanyName || '',
        CurrentStage: source.CurrentStage || 1,
        CustomerMail: source.CustomerMail || '',
        CustomerName: source.CustomerName || '',
        Descriptions: source.Descriptions || [{ Stage: 1, Text: '', Timestamp: '', New: false }],
        IsClosed: source.IsClosed || false,
        IsTagged: source.IsTagged || false,
        PhoneNumber: source.PhoneNumber || '',
        UserId: source.UserId || null,
        Users: source.Users || (source.UserId ? [{
            UserId: source.UserId,
            Name: source.CustomerName || '',
            Email: source.CustomerMail || '',
            PhoneNumber: source.PhoneNumber || '',
        }] : []),
        CaseManager: source.CaseManager || '',
        CaseManagerId: source.CaseManagerId || '',
        EstimatedCompletionDate: source.EstimatedCompletionDate,
        LicenseExpiryDate: source.LicenseExpiryDate,
        HasLicenseExpiry: source.HasLicenseExpiry || false,
    };
}

export default function CaseFullView({ caseDetails, initialDraft, rePerformRequest, onFailureFunction, closePopUpFunction, onCaseCreated, style: _style }) {
    const { t } = useTranslation();
    const { openPopup: openConfirm, closePopup: closeConfirm } = usePopup();
    const { canAction } = useFirmPermissions() || { canAction: () => true };
    const canCreateCases = canAction('cases', 'create');
    const canEditCases = canAction('cases', 'edit');
    const canDeleteCases = canAction('cases', 'delete');
    const [caseHasBeenChosen, setCaseHasBeenChosen] = useState(false)
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});
    const [saveError, setSaveError] = useState('');
    const [caseData, setCaseData] = useState(() => _buildInitialCaseData(caseDetails, initialDraft));
    const isEditingExisting = !!caseDetails?.CaseId || (caseHasBeenChosen && !!caseData.CaseId);
    const canSaveCase = isEditingExisting ? canEditCases : canCreateCases;
    const caseFormReadOnly = isEditingExisting && !canEditCases;
    const [signingOpen, setSigningOpen] = useState(false);
    const signingAvailable = useSigningV2Available();

    const seed = caseDetails?.CaseId ? caseDetails : (initialDraft || caseDetails);
    const [showLicenseExpiry, setShowLicenseExpiry] = useState(!!seed?.HasLicenseExpiry || !!seed?.LicenseExpiryDate);

    const moveStage = (fromIndex, toIndex) => {
        setCaseData((prev) => {
            const descs = [...prev.Descriptions];
            const [moved] = descs.splice(fromIndex, 1);
            descs.splice(toIndex, 0, moved);
            return { ...prev, Descriptions: descs.map((d, i) => ({ ...d, Stage: i + 1 })) };
        });
    };

    // ——— Drag & drop for stages ———
    const [dragIdx, setDragIdx] = useState(null);
    const [overIdx, setOverIdx] = useState(null);
    const touchDrag = useRef({ active: false });

    const handleDragStart = (e, index) => { setDragIdx(index); e.dataTransfer.effectAllowed = 'move'; };
    const handleDragOver = (e, index) => { e.preventDefault(); if (overIdx !== index) setOverIdx(index); };
    const handleDrop = (e, index) => { e.preventDefault(); if (dragIdx !== null && dragIdx !== index) moveStage(dragIdx, index); setDragIdx(null); setOverIdx(null); };
    const handleDragEnd = () => { setDragIdx(null); setOverIdx(null); };

    const handleTouchDragStart = (index) => { touchDrag.current.active = true; setDragIdx(index); };
    const handleTouchDragMove = (e) => {
        if (!touchDrag.current.active) return;
        e.preventDefault();
        const touch = e.touches[0];
        const els = document.elementsFromPoint(touch.clientX, touch.clientY);
        for (const el of els) { if (el.dataset?.stageIndex != null) { setOverIdx(Number(el.dataset.stageIndex)); break; } }
    };
    const handleTouchDragEnd = () => {
        if (touchDrag.current.active && dragIdx !== null && overIdx !== null && dragIdx !== overIdx) moveStage(dragIdx, overIdx);
        touchDrag.current.active = false; setDragIdx(null); setOverIdx(null);
    };

    const validateCaseData = useMemo(() => {
        return (data) => {
            const errors = {};

            const caseName = String(data?.CaseName || '').trim();
            if (!caseName) errors.CaseName = t('errors.required');

            const caseTypeName = String(data?.CaseTypeName || '').trim();
            if (!caseTypeName) errors.CaseTypeName = t('errors.required');

            const customerName = String(data?.CustomerName || '').trim();
            if (!customerName) errors.CustomerName = t('errors.required');

            const phoneRequired = true;
            const phone = String(data?.PhoneNumber || '').trim();
            if (phoneRequired && !phone) {
                errors.PhoneNumber = t('errors.required');
            } else {
                const phoneError = IsraeliPhoneNumberValidation(phone);
                if (phoneError) errors.PhoneNumber = phoneError;
            }

            const email = String(data?.CustomerMail || '').trim();
            const emailErr = emailValidation(email);
            if (emailErr) errors.CustomerMail = emailErr;

            const caseManager = String(data?.CaseManagerId || '').trim();
            if (!caseManager) errors.CaseManager = t('errors.required');

            const courtNumber = String(data?.CourtCaseNumber || '').normalize('NFKC').replace(/[\u200e\u200f\u202a-\u202e\u2066-\u2069]/g, '').replace(/[‐‑‒–—−]/g, '-').trim();
            if (courtNumber && (!/^\d{1,7}-(0[1-9]|1[0-2])-\d{2}$/.test(courtNumber) || Number(courtNumber.split('-')[0]) === 0)) {
                errors.CourtCaseNumber = t('cases.invalidCourtCaseNumber');
            }

            return errors;
        };
    }, [t]);

    const applyFieldErrorUpdates = (partialUpdates) => {
        if (!hasSubmitted) return;

        const nextData = { ...caseData, ...partialUpdates };
        setFieldErrors(validateCaseData(nextData));
    };

    const [showAddClientPopup, setShowAddClientPopup] = useState(false);
    const [addClientPopupKey, setAddClientPopupKey] = useState(0);
    const [addClientInitials, setAddClientInitials] = useState({
        name: '',
        phone: '',
        email: '',
        companyName: '',
    });
    const [clientSearchDraft, setClientSearchDraft] = useState('');

    const handleOpenAddClientPopup = useCallback((draftName) => {
        const safeDraft = typeof draftName === 'string' ? draftName : '';
        const name = String(safeDraft || clientSearchDraft || caseData.CustomerName || '').trim();
        setAddClientInitials({
            name,
            phone: String(caseData.PhoneNumber || '').trim(),
            email: String(caseData.CustomerMail || '').trim(),
            companyName: String(caseData.CompanyName || '').trim(),
        });
        setAddClientPopupKey((k) => k + 1);
        setShowAddClientPopup(true);
    }, [
        clientSearchDraft,
        caseData.CustomerName,
        caseData.PhoneNumber,
        caseData.CustomerMail,
        caseData.CompanyName,
    ]);

    const handleClientSavedFromPopup = useCallback((savedClient) => {
        if (!savedClient?.UserId) return;
        setCaseData((prev) => {
            if (prev.Users.some(u => u.UserId === savedClient.UserId)) return prev;
            const updatedUsers = [...prev.Users, {
                UserId: savedClient.UserId,
                Name: savedClient.Name,
                Email: savedClient.Email,
                PhoneNumber: savedClient.PhoneNumber,
            }];
            const primary = updatedUsers[0];
            return {
                ...prev,
                Users: updatedUsers,
                UserId: primary.UserId,
                CustomerName: primary.Name,
                CustomerMail: primary.Email || prev.CustomerMail,
                PhoneNumber: primary.PhoneNumber || prev.PhoneNumber,
                CompanyName: savedClient.CompanyName || prev.CompanyName,
            };
        });
        setFieldErrors((prev) => {
            if (!prev.CustomerName && !prev.PhoneNumber) return prev;
            const next = { ...prev };
            delete next.CustomerName;
            delete next.PhoneNumber;
            return next;
        });
    }, []);

    const { result: customers, isPerforming: isPerformingCustomers, performRequest: searchCustomers } = useAutoHttpRequest(customersApi.getCustomersByName, { onFailure: () => { } });

    const handleCustomerSearch = useCallback((query) => {
        setClientSearchDraft(query);
        searchCustomers(query);
    }, [searchCustomers]);

    const { result: caseTypes, isPerforming: isPerformingCaseTypes, performRequest: searchCaseTypes } = useAutoHttpRequest(casesTypeApi.getCaseTypeByName, { onFailure: () => { } });

    const { result: adminByName, isPerforming: isPerformingGetAdmin, performRequest: getAdminByName } = useAutoHttpRequest(adminApi.getStaffByName, { onFailure: () => { } });

    const { result: cases, isPerforming: isPerformingCases, performRequest: searchCases } = useAutoHttpRequest(casesApi.getCaseByName, { onFailure: () => { } });

    const { isPerforming: isSaving, performRequest: saveCase } = useHttpRequest(
        isEditingExisting ? casesApi.updateCaseById : casesApi.addCase,
        (data) => {
            rePerformRequest?.();
            (async () => {
                try {
                    if (!isEditingExisting && onCaseCreated && data?.caseId) {
                        await onCaseCreated(data.caseId);
                    }
                    closePopUpFunction?.();
                } catch (err) {
                    onFailureFunction?.({
                        data: { message: err?.message },
                        message: err?.message,
                    });
                }
            })();
        },
        (error) => {
            setSaveError(error?.data?.message || error?.response?.data?.message || error?.message || t('cases.saveFailed'));
            onFailureFunction?.(error);
        }
    );

    const { isPerforming: isDeleting, performRequest: deleteCase } = useHttpRequest(
        casesApi.deleteCaseById,
        () => {
            rePerformRequest?.();
            closePopUpFunction?.();
        },
        onFailureFunction
    );

    const handleInputChange = (field, value) => {
        setCaseData((prevDetails) => {
            const update = { ...prevDetails, [field]: value };

            // When CurrentStage is rolled back, clear timestamps and IsNew
            // on descriptions that are now ahead of the new stage,
            // and also un-close the case.
            if (field === 'CurrentStage') {
                const newStage = Number(value) || 0;
                if (newStage > 0 && Array.isArray(prevDetails.Descriptions)) {
                    update.Descriptions = prevDetails.Descriptions.map((d) => {
                        const descStage = Number(d.Stage) || 0;
                        if (descStage > newStage) {
                            return { ...d, Timestamp: null, IsNew: false };
                        }
                        return d;
                    });
                }
                // If we're going backwards, un-close the case
                if (newStage > 0 && prevDetails.IsClosed) {
                    update.IsClosed = false;
                }
            }

            return update;
        });
        applyFieldErrorUpdates({ [field]: value });
    };

    const handleCaseTypeSelect = (caseTypeName, resultItem) => {
        const selectedCaseType = resultItem || caseTypes?.find(type => type.CaseTypeName?.trim() === caseTypeName?.trim());

        if (selectedCaseType) {
            setCaseData((prevDetails) => ({
                ...prevDetails,
                CaseTypeName: selectedCaseType.CaseTypeName,
                Stages: selectedCaseType.NumberOfStages,
                Descriptions: selectedCaseType.Descriptions || [{ Stage: 1, Text: '', Timestamp: '', New: false }],
                CaseTypeId: selectedCaseType.CaseTypeId
            }));

            applyFieldErrorUpdates({
                CaseTypeName: selectedCaseType.CaseTypeName,
                CaseTypeId: selectedCaseType.CaseTypeId,
            });
        }
    };

    const handleSaveCase = () => {
        setSaveError('');
        setHasSubmitted(true);
        const errors = validateCaseData(caseData);
        setFieldErrors(errors);
        if (Object.keys(errors).length) return;

        const payload = { ...caseData, UserIds: caseData.Users.map(u => u.UserId) };
        saveCase(payload);
    };

    const handleUpdateCase = () => {
        setSaveError('');
        setHasSubmitted(true);
        const errors = validateCaseData(caseData);
        setFieldErrors(errors);
        if (Object.keys(errors).length) return;

        const payload = { ...caseData, UserIds: caseData.Users.map(u => u.UserId) };
        saveCase(caseData.CaseId, payload);
    };

    const handleDeleteCase = () => {
        openConfirm(
            <ConfirmationDialog
                title={t('cases.deleteCase')}
                message={t('cases.deleteCaseConfirm')}
                confirmText={t('cases.deleteCase')}
                cancelText={t('common.cancel')}
                danger
                onCancel={closeConfirm}
                onConfirm={() => {
                    closeConfirm();
                    deleteCase(caseData.CaseId);
                }}
            />
        );
    };

    const handleCustomerSelect = (userName, resultItem) => {
        const selectedUser = resultItem || customers?.find(user => user.Name?.trim() === userName?.trim());

        if (selectedUser) {
            setCaseData((prevDetails) => {
                if (prevDetails.Users.some(u => u.UserId === selectedUser.UserId)) return prevDetails;

                const updatedUsers = [...prevDetails.Users, {
                    UserId: selectedUser.UserId,
                    Name: selectedUser.Name,
                    Email: selectedUser.Email,
                    PhoneNumber: selectedUser.PhoneNumber,
                }];

                // Primary client = first user
                const primary = updatedUsers[0];
                return {
                    ...prevDetails,
                    Users: updatedUsers,
                    UserId: primary.UserId,
                    CustomerName: primary.Name,
                    CustomerMail: primary.Email || prevDetails.CustomerMail,
                    PhoneNumber: primary.PhoneNumber || prevDetails.PhoneNumber,
                    CompanyName: selectedUser.CompanyName || prevDetails.CompanyName,
                };
            });

            applyFieldErrorUpdates({
                CustomerName: selectedUser.Name,
                CustomerMail: selectedUser.Email,
                PhoneNumber: selectedUser.PhoneNumber,
            });
        }
    };

    const handleRemoveUser = (userId) => {
        setCaseData((prevDetails) => {
            const updatedUsers = prevDetails.Users.filter(u => u.UserId !== userId);
            const primary = updatedUsers[0];
            return {
                ...prevDetails,
                Users: updatedUsers,
                UserId: primary?.UserId || null,
                CustomerName: primary?.Name || '',
                CustomerMail: primary?.Email || '',
                PhoneNumber: primary?.PhoneNumber || '',
            };
        });
    };

    const handleManagerSelected = (selectedManager, resultItem) => {
        const selectedAdmin = resultItem || (Array.isArray(adminByName) ? adminByName : []).find(admin => admin.name?.trim() === selectedManager?.trim());
        if (!selectedAdmin) return;

        setCaseData((prevDetails) => ({
            ...prevDetails,
            CaseManager: selectedAdmin.name,
            CaseManagerId: selectedAdmin.userid
        }));

        applyFieldErrorUpdates({ CaseManagerId: selectedAdmin.userid });
    }

    const handleCaseSelect = (selectedCase, resultItem) => {
        setCaseData((prevDetails) => ({
            ...prevDetails,
            CaseName: selectedCase
        }));

        applyFieldErrorUpdates({ CaseName: selectedCase });

        const caseDetails = resultItem || cases.find(c => c.CaseName?.trim() === selectedCase?.trim());
        if (caseDetails) {
            setCaseHasBeenChosen(true);
            setCaseData({
                CaseId: caseDetails.CaseId || '',
                CaseName: caseDetails.CaseName || '',
                CourtCaseNumber: caseDetails.CourtCaseNumber || '',
                CaseTypeId: caseDetails.CaseTypeId || '',
                CaseTypeName: caseDetails.CaseTypeName || '',
                CompanyName: caseDetails.CompanyName || '',
                CurrentStage: caseDetails.CurrentStage || 1,
                CustomerMail: caseDetails.CustomerMail || '',
                CustomerName: caseDetails.CustomerName || '',
                Descriptions: caseDetails.Descriptions || [{ Stage: 1, Text: '', Timestamp: '', New: false }],
                IsClosed: caseDetails.IsClosed || false,
                IsTagged: caseDetails.IsTagged || false,
                PhoneNumber: caseDetails.PhoneNumber || '',
                UserId: caseDetails.UserId,
                Users: caseDetails.Users || (caseDetails.UserId ? [{ UserId: caseDetails.UserId, Name: caseDetails.CustomerName || '', Email: caseDetails.CustomerMail || '', PhoneNumber: caseDetails.PhoneNumber || '' }] : []),
                CaseManager: caseDetails.CaseManager || '',
                CaseManagerId: caseDetails?.CaseManagerId || '',
                EstimatedCompletionDate: caseDetails.EstimatedCompletionDate,
                LicenseExpiryDate: caseDetails.LicenseExpiryDate,
                HasLicenseExpiry: caseDetails.HasLicenseExpiry || false,
            });
        }
    };

    if (signingOpen) return <CaseSigningFlow caseId={caseDetails?.CaseId || caseData.CaseId} onBack={() => setSigningOpen(false)} />;
    return (
        <>
        <SimpleContainer className={`lw-caseFullView${caseFormReadOnly ? ' lw-caseFullView--readOnly' : ''}`}>
            {saveError && <p role="alert">{saveError}</p>}
            <SimpleScrollView>
                <SimpleContainer className="lw-caseFullView__row">
                    {isEditingExisting ?
                        <SimpleInput
                            className="lw-caseFullView__field"
                            title={t('cases.caseNumber')}
                            value={caseData.CaseName}
                            onChange={(e) => handleInputChange('CaseName', e.target.value)}
                            error={fieldErrors?.CaseName}
                        />
                        :
                        <SearchInput
                            onSearch={(caseName) => {
                                searchCases(caseName); setCaseData((prevDetails) => ({
                                    ...prevDetails,
                                    CaseName: caseName
                                }));

                                applyFieldErrorUpdates({ CaseName: caseName });
                            }}
                            title={t('cases.caseNumber')}
                            value={caseData.CaseName}
                            isPerforming={isPerformingCases}
                            getButtonTextFunction={(item) => item.CaseName}
                            buttonPressFunction={handleCaseSelect}
                            queryResult={cases}
                            className="lw-caseFullView__field"
                            error={fieldErrors?.CaseName}
                        />
                    }

                    <SearchInput
                        onSearch={searchCaseTypes}
                        title={t('cases.caseType')}
                        value={caseData.CaseTypeName}
                        isPerforming={isPerformingCaseTypes}
                        getButtonTextFunction={(item) => item.CaseTypeName}
                        buttonPressFunction={handleCaseTypeSelect}
                        queryResult={caseTypes}
                        className="lw-caseFullView__field"
                        error={fieldErrors?.CaseTypeName}
                    />
                </SimpleContainer>

                <SimpleContainer className="lw-caseFullView__row">
                    <SimpleContainer className="lw-caseFullView__field lw-caseFullView__clientsCol">
                        <SimpleContainer className="lw-caseFullView__clientFieldWrap">
                            <SearchInput
                                onSearch={handleCustomerSearch}
                                title={t('cases.customerSearchHint', { defaultValue: 'שם לקוח' })}
                                placeholder={t('cases.customerSearchPlaceholder', { defaultValue: 'חיפוש לפי שם, טלפון, אימייל או חברה' })}
                                value={caseData.CustomerName}
                                isPerforming={isPerformingCustomers}
                                getButtonTextFunction={(item) => `${item.Name}${item.PhoneNumber ? ` - ${item.PhoneNumber}` : ''}`}
                                getSelectValueFunction={(item) => item.Name}
                                buttonPressFunction={handleCustomerSelect}
                                queryResult={customers}
                                error={fieldErrors?.CustomerName}
                                emptyActionText={t('cases.addNewClient', { defaultValue: '+ הוספת לקוח חדש' })}
                                onEmptyAction={handleOpenAddClientPopup}
                            />
                            <button
                                type="button"
                                className="lw-caseFullView__addClientBtn"
                                onClick={() => handleOpenAddClientPopup()}
                                title={t('cases.addNewClient', { defaultValue: '+ הוספת לקוח חדש' })}
                                aria-label={t('cases.addNewClient', { defaultValue: '+ הוספת לקוח חדש' })}
                            >
                                +
                            </button>
                        </SimpleContainer>
                        {caseData.Users.length > 0 && (
                            <SimpleContainer className="lw-caseFullView__clientChips">
                                {caseData.Users.map((u) => (
                                    <span key={u.UserId} className="lw-caseFullView__clientChip">
                                        {u.Name}
                                        <button type="button" className="lw-caseFullView__chipRemove" onClick={() => handleRemoveUser(u.UserId)}>&times;</button>
                                    </span>
                                ))}
                            </SimpleContainer>
                        )}
                    </SimpleContainer>
                    <SimpleInput
                        className="lw-caseFullView__field"
                        title={t('cases.companyName')}
                        value={caseData.CompanyName}
                        onChange={(e) => handleInputChange('CompanyName', e.target.value)}
                    />
                </SimpleContainer>

                <SimpleContainer className="lw-caseFullView__row">
                    <SimpleInput
                        className="lw-caseFullView__field"
                        title={t('cases.phoneNumber')}
                        value={caseData.PhoneNumber}
                        onChange={(e) => handleInputChange('PhoneNumber', e.target.value)}
                        error={fieldErrors?.PhoneNumber}
                    />
                    <SimpleInput
                        className="lw-caseFullView__field"
                        title={t('cases.currentStage')}
                        value={caseData.CurrentStage}
                        onChange={(e) => handleInputChange('CurrentStage', e.target.value)}
                    />
                </SimpleContainer>

                <SimpleContainer className="lw-caseFullView__row">
                    <SimpleInput
                        className="lw-caseFullView__field"
                        title={t('cases.courtCaseNumber')}
                        value={caseData.CourtCaseNumber}
                        error={fieldErrors?.CourtCaseNumber}
                        onChange={(e) => handleInputChange('CourtCaseNumber', e.target.value)}
                        timeToWaitInMilli={0}
                        dir="ltr"
                        placeholder="12345-01-26"
                    />
                </SimpleContainer>
                <SimpleContainer className="lw-caseFullView__row">
                    <SimpleInput
                        className="lw-caseFullView__field"
                        title={t('cases.customerEmail')}
                        type="email"
                        value={caseData.CustomerMail}
                        onChange={(e) => handleInputChange('CustomerMail', e.target.value)}
                        error={fieldErrors?.CustomerMail}
                    />
                    <SearchInput
                        onSearch={getAdminByName}
                        title={t('cases.caseManager')}
                        value={caseData.CaseManager}
                        isPerforming={isPerformingGetAdmin}
                        getButtonTextFunction={(item) => item.name}
                        buttonPressFunction={handleManagerSelected}
                        queryResult={adminByName}
                        className="lw-caseFullView__field"
                        error={fieldErrors?.CaseManager}
                    />
                </SimpleContainer>

                <SimpleContainer className="lw-caseFullView__row">
                    <SimpleInput
                        className="lw-caseFullView__field"
                        title={t('cases.estimatedCompletionDate')}
                        type="date"
                        value={toNativeDateValue(caseData.EstimatedCompletionDate)}
                        onChange={(e) => handleInputChange('EstimatedCompletionDate', e.target.value || null)}
                    />
                    <SimpleContainer className="lw-caseFullView__field lw-caseFullView__licenseToggleField">
                        <label className="lw-caseFullView__toggleLabel">
                            <input
                                type="checkbox"
                                checked={showLicenseExpiry}
                                onChange={(e) => {
                                    const on = e.target.checked;
                                    setShowLicenseExpiry(on);
                                    handleInputChange('HasLicenseExpiry', on);
                                    if (!on) handleInputChange('LicenseExpiryDate', null);
                                }}
                            />
                            <span>{t('cases.licenseExpiryDate')}</span>
                        </label>
                    </SimpleContainer>
                </SimpleContainer>

                {showLicenseExpiry && (
                    <SimpleContainer className="lw-caseFullView__row">
                        <SimpleInput
                            className="lw-caseFullView__field"
                            title={t('cases.licenseExpiryDate')}
                            type="date"
                            value={toNativeDateValue(caseData.LicenseExpiryDate)}
                            onChange={(e) => handleInputChange('LicenseExpiryDate', e.target.value || null)}
                        />
                    </SimpleContainer>
                )}

                {caseData?.Descriptions?.map((description, index) => (
                    <SimpleContainer
                        key={`DescriptionNumber${description.DescriptionId || index}`}
                        className={`lw-caseFullView__textAreaRow${overIdx === index && dragIdx !== index ? ' lw-caseFullView__textAreaRow--dragOver' : ''}${dragIdx === index ? ' lw-caseFullView__textAreaRow--dragging' : ''}`}
                        data-stage-index={index}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDrop={(e) => handleDrop(e, index)}
                    >
                        <SimpleContainer className="lw-caseFullView__stageHeader">
                            <span
                                className="lw-caseFullView__dragHandle"
                                draggable
                                onDragStart={(e) => handleDragStart(e, index)}
                                onDragEnd={handleDragEnd}
                                onTouchStart={() => handleTouchDragStart(index)}
                                onTouchMove={(e) => handleTouchDragMove(e)}
                                onTouchEnd={handleTouchDragEnd}
                            >&#x2630;</span>
                            <SimpleContainer className="lw-caseFullView__stageArrows">
                                {index > 0 && (
                                    <button type="button" className="lw-caseFullView__arrowBtn" onClick={() => moveStage(index, index - 1)} title={t('common.moveUp')}>&#x25B2;</button>
                                )}
                                {index < caseData.Descriptions.length - 1 && (
                                    <button type="button" className="lw-caseFullView__arrowBtn" onClick={() => moveStage(index, index + 1)} title={t('common.moveDown')}>&#x25BC;</button>
                                )}
                            </SimpleContainer>
                            {caseData.Descriptions.length > 1 && (
                                <button
                                    type="button"
                                    className="lw-caseFullView__removeStageBtn"
                                    onClick={() => {
                                        setCaseData((prev) => {
                                            const updated = prev.Descriptions.filter((_, i) => i !== index)
                                                .map((d, i) => ({ ...d, Stage: i + 1 }));
                                            return { ...prev, Descriptions: updated };
                                        });
                                    }}
                                    title={t('cases.removeStage')}
                                >&#x2715;</button>
                            )}
                        </SimpleContainer>
                        <SimpleTextArea
                            title={t('cases.descriptionNumber', { number: index + 1 })}
                            value={description?.Text || ''}
                            onChange={(text) => {
                                setCaseData((prevDetails) => {
                                    const updatedDescriptions = prevDetails.Descriptions.map((d, i) =>
                                        i === index ? { ...d, Text: text } : d
                                    );
                                    return { ...prevDetails, Descriptions: updatedDescriptions };
                                });
                            }}
                        />

                    </SimpleContainer>
                ))}

                <SimpleContainer className="lw-caseFullView__addStageRow">
                    <SecondaryButton
                        onPress={() => {
                            setCaseData((prev) => {
                                const nextStage = (prev.Descriptions?.length || 0) + 1;
                                return {
                                    ...prev,
                                    Descriptions: [
                                        ...(prev.Descriptions || []),
                                        { Stage: nextStage, Text: '', Timestamp: null, IsNew: false },
                                    ],
                                };
                            });
                        }}
                        size={buttonSizes.SMALL}
                        style={{ height: 20, padding: '0.2rem 0.5rem', fontSize: '0.7rem' }}
                    >
                        {t('cases.addStage')}
                    </SecondaryButton>
                </SimpleContainer>

                <SimpleContainer className="lw-caseFullView__buttonsRow">
                    {signingAvailable && (caseDetails?.CaseId || caseData.CaseId) && canAction('signing', 'upload') && <SecondaryButton
                        onPress={() => setSigningOpen(true)} size={buttonSizes.MEDIUM}>
                        {t('signingV2.compose.context.open')}
                    </SecondaryButton>}
                    {(isEditingExisting || caseHasBeenChosen) && canDeleteCases && (
                        <SecondaryButton
                            onPress={handleDeleteCase}
                            isPerforming={isDeleting}
                            size={buttonSizes.MEDIUM}
                        >
                            {isDeleting ? t('common.deleting') : t('cases.deleteCase')}
                        </SecondaryButton>
                    )}
                    {canSaveCase && (
                    <PrimaryButton
                        onPress={isEditingExisting ? handleUpdateCase : handleSaveCase}
                        isPerforming={isSaving}
                        size={buttonSizes.MEDIUM}
                    >
                        {isSaving ? t('common.saving') : isEditingExisting || caseHasBeenChosen ? t('cases.updateCase') : t('cases.saveCase')}
                    </PrimaryButton>
                    )}
                    <SecondaryButton
                        onPress={() => closePopUpFunction?.()}
                        size={buttonSizes.MEDIUM}
                        className="lw-cancelButton"
                    >
                        {t('common.cancel')}
                    </SecondaryButton>
                </SimpleContainer>
            </SimpleScrollView>
        </SimpleContainer>
        <SimplePopUp
            isOpen={showAddClientPopup}
            onClose={() => setShowAddClientPopup(false)}
        >
            <ClientPopup
                key={addClientPopupKey}
                initialName={addClientInitials.name}
                initialPhone={addClientInitials.phone}
                initialEmail={addClientInitials.email}
                initialCompanyName={addClientInitials.companyName}
                rePerformRequest={handleClientSavedFromPopup}
                closePopUpFunction={() => setShowAddClientPopup(false)}
            />
        </SimplePopUp>
    </>
    );
}
